import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Smartphone, Monitor, Trash2, Globe, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Trusted-device management panel (iter 74 — completes the iter-72 backend).
 *
 * Lists all of the signed-in user's active long-lived "stay signed in" devices
 * and lets them revoke each one individually. Revoking the current device
 * also clears its cookie server-side so the user is logged out cleanly.
 *
 * Designed as a self-contained section so the same component can drop into
 * Admin → Settings (this iter) and the Clinic Portal → Account page (future).
 * Pass a `title` / `subtitle` override if the surrounding page needs a
 * different framing, otherwise defaults work for both surfaces.
 */
export function TrustedDevicesPanel({
    title = "Sign-in security",
    subtitle = "Devices currently allowed to skip the 6-digit code when signing in.",
}) {
    const [devices, setDevices] = useState(null);
    const [loading, setLoading] = useState(true);
    const [revoking, setRevoking] = useState(null);  // id of device currently being revoked
    // Two-step confirm so a fat-finger click doesn't accidentally sign the user
    // out of the very browser they're managing devices from. `pending` holds
    // the device-id the user clicked Revoke on.
    const [pending, setPending] = useState(null);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await axios.get(`${API}/auth/trusted-devices`);
            setDevices(Array.isArray(data) ? data : []);
        } catch (e) {
            console.warn("trusted-devices: load failed:", e?.message || e);
            setDevices([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const confirmRevoke = async (device) => {
        setRevoking(device.id);
        try {
            await axios.delete(`${API}/auth/trusted-devices/${device.id}`);
            toast.success(`Revoked “${device.label}”`);
            // If the user revoked their CURRENT device, the backend clears
            // the cookie — but the auth JWT is still valid until the access
            // token expires (15 min). Reload the list to reflect the new
            // state; a hard sign-out is intentional next-step for the user.
            await load();
            if (device.is_current) {
                toast.info("This device is no longer trusted. You'll need to sign in again next time.", { duration: 6000 });
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not revoke device");
        } finally {
            setRevoking(null);
            setPending(null);
        }
    };

    if (loading) {
        return (
            <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="trusted-devices-panel">
                <div className="flex items-center gap-2 text-[13.5px] text-slate-500">
                    <Loader2 size={14} className="animate-spin" /> Loading devices…
                </div>
            </div>
        );
    }

    return (
        <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="trusted-devices-panel">
            <div className="flex items-start gap-3 mb-4">
                <div className="h-10 w-10 rounded-xl bg-cyan-50 text-cyan-700 flex items-center justify-center shrink-0">
                    <ShieldCheck size={18} />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="font-display text-[17px] tracking-tight text-slate-900">{title}</div>
                    <p className="text-[12.5px] text-slate-500 mt-0.5">{subtitle}</p>
                </div>
                <button
                    onClick={load}
                    data-testid="trusted-devices-refresh"
                    className="text-[11.5px] font-mono-tr tracking-wider uppercase text-slate-400 hover:text-slate-700"
                >
                    Refresh
                </button>
            </div>

            {devices.length === 0 ? (
                <div className="rounded-xl bg-slate-50 border border-slate-100 p-5 text-center" data-testid="trusted-devices-empty">
                    <div className="text-[13.5px] text-slate-600">
                        No trusted devices yet.
                    </div>
                    <div className="text-[11.5px] text-slate-400 mt-1.5 leading-snug">
                        Tick &ldquo;Remember this device for 90 days&rdquo; on the sign-in screen next time you log in, and it&rsquo;ll show up here.
                    </div>
                </div>
            ) : (
                <ul className="divide-y divide-slate-100" data-testid="trusted-devices-list">
                    {devices.map((d) => (
                        <DeviceRow
                            key={d.id}
                            device={d}
                            pending={pending === d.id}
                            revoking={revoking === d.id}
                            onAskRevoke={() => setPending(d.id)}
                            onCancelRevoke={() => setPending(null)}
                            onConfirmRevoke={() => confirmRevoke(d)}
                        />
                    ))}
                </ul>
            )}
        </div>
    );
}

function DeviceIcon({ label }) {
    // Friendly icon based on the parsed UA label the backend produces.
    if (/iPhone|Android/i.test(label)) return <Smartphone size={16} className="text-slate-500" />;
    if (/Mac|Windows|Linux/i.test(label)) return <Monitor size={16} className="text-slate-500" />;
    return <Globe size={16} className="text-slate-500" />;
}

function DeviceRow({ device, pending, revoking, onAskRevoke, onCancelRevoke, onConfirmRevoke }) {
    return (
        <li className="py-3.5 flex items-center gap-3" data-testid={`device-row-${device.id}`}>
            <div className="h-9 w-9 rounded-lg bg-slate-50 border border-slate-100 flex items-center justify-center shrink-0">
                <DeviceIcon label={device.label} />
            </div>
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[14px] font-medium text-slate-900 truncate">{device.label}</span>
                    {device.is_current && (
                        <span className="px-1.5 py-0.5 rounded-full text-[10px] font-mono-tr tracking-wider uppercase bg-emerald-50 text-emerald-700 border border-emerald-100" data-testid="device-current-badge">
                            This device
                        </span>
                    )}
                </div>
                <div className="text-[11.5px] text-slate-500 mt-0.5 font-mono-tr">
                    Last used {fmtRelative(device.last_used_at)} · expires {fmtAbsolute(device.expires_at)}
                    {device.last_ip ? ` · IP ${device.last_ip}` : ""}
                </div>
            </div>
            {pending ? (
                <div className="flex items-center gap-2 shrink-0">
                    <span className="text-[11.5px] text-rose-600 inline-flex items-center gap-1">
                        <AlertTriangle size={12} /> Sure?
                    </span>
                    <button
                        onClick={onCancelRevoke}
                        disabled={revoking}
                        data-testid={`device-cancel-${device.id}`}
                        className="px-2.5 py-1 rounded-md text-[12px] text-slate-600 hover:bg-slate-100 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirmRevoke}
                        disabled={revoking}
                        data-testid={`device-confirm-revoke-${device.id}`}
                        className="px-2.5 py-1 rounded-md text-[12px] font-medium bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-60 inline-flex items-center gap-1.5"
                    >
                        {revoking ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />}
                        Revoke
                    </button>
                </div>
            ) : (
                <button
                    onClick={onAskRevoke}
                    data-testid={`device-revoke-${device.id}`}
                    className="shrink-0 px-2.5 py-1 rounded-md text-[12px] text-slate-500 hover:text-rose-700 hover:bg-rose-50 transition-colors inline-flex items-center gap-1.5"
                >
                    <Trash2 size={12} /> Revoke
                </button>
            )}
        </li>
    );
}

// Date helpers — kept local so the panel is self-contained.
function fmtRelative(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        const diffMs = Date.now() - d.getTime();
        const sec = Math.round(diffMs / 1000);
        if (sec < 60) return "just now";
        const min = Math.round(sec / 60);
        if (min < 60) return `${min} min ago`;
        const hr = Math.round(min / 60);
        if (hr < 24) return `${hr} hr ago`;
        const day = Math.round(hr / 24);
        if (day < 30) return `${day} day${day === 1 ? "" : "s"} ago`;
        return d.toLocaleDateString();
    } catch { return iso; }
}

function fmtAbsolute(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
    } catch { return iso; }
}

export default TrustedDevicesPanel;
